import {Card, Spin, Table} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {UserResponse, Organization} from "../api-client";
import {FormattedMessage, useIntl} from "react-intl";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {getAccountStatus} from '../i18n/utils';
import dayjs from "dayjs";
import {getUserApi, getOrganizationApi} from "../api/apiInstances";
import logger from "../utils/logger";
import {getCountry} from "../i18n/countries";

const Users = () => {

    const intl = useIntl();
    const {locale} = useContext(DentobookApplicationContext);

    const [users, setUsers] = useState<UserResponse[]>([]);
    const [loading, setLoading] = useState(true);

    const userApi = getUserApi();
    const organizationApi = getOrganizationApi();

    useEffect(() => {
        organizationApi.fetchAllOrganizations('')
            .then((organizationResponse) => {
                userApi.fetchAllUsersLastLogin()
                    .then((usersLastLoginResponse) => {
                        userApi.fetchAllUsers()
                            .then((response) => {
                                const tableData = response.data.map((item: UserResponse, idx: number) => {
                                    const organization = organizationResponse.data.find(organization => item.organizationId === organization.id);
                                    const lastLoginTimestamp = usersLastLoginResponse.data
                                        .find(lastLoginItem => lastLoginItem.id === item.id)?.lastLoginTimestamp!;
                                    const loginCountTimestamp = usersLastLoginResponse.data
                                        .find(lastLoginItem => lastLoginItem.id === item.id)?.loginCount;
                                    let lastLoginDate = "";
                                    if (lastLoginTimestamp) {
                                        lastLoginDate = dayjs(new Date(lastLoginTimestamp)).format('DD.MM.YYYY hh:mm:ss');
                                    }
                                    const additionalFields: {
                                        lastLogin: string,
                                        loginCount: string,
                                        country: string | undefined,
                                        accountStatus: string
                                    } = {
                                        lastLogin: lastLoginDate,
                                        loginCount: loginCountTimestamp ? loginCountTimestamp?.toString() : '',
                                        country: '',
                                        accountStatus: ''
                                    };
                                    if (organization) {
                                        const accountStatus = getAccountStatus(organization!, intl);
                                        const countryData = getCountry(organization.country);
                                        const countryName = countryData ? (locale === 'BG' ? countryData.name_bg : countryData.name_en) : organization.country;
                                        additionalFields['country'] = countryName ?? undefined;
                                        additionalFields['accountStatus'] = accountStatus;
                                    }

                                    return {
                                        key: idx, ...item, ...additionalFields
                                    };
                                });

                                setUsers(tableData);
                                setLoading(false);
                            })
                            .catch((error) => {
                                setLoading(false);
                                if (error.code === "ERR_NETWORK") {
                                    window.location.href = `${window.location.origin.replace(window.location.port, '9000')}`;
                                }
                            })
                    })
                    .catch(err => {
                        setLoading(false);
                        logger.log(err);
                    })
            })
            .catch((error) => {
                setLoading(false);
                if (error.code === "ERR_NETWORK") {
                    window.location.href = `${window.location.origin.replace(window.location.port, '9000')}`;
                }
            })
    }, [locale]);

    return (
        <AccessControl accessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR]}>
            <Card
                title={<FormattedMessage id="users"/>}
                bordered={false}
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin tip={intl.formatMessage({ id: 'loading' })} />
                    </div>
                ) : (
                    <Table
                    dataSource={users}
                    pagination={{ pageSize: 50 }}
                    className="dentobook-table"
                >
                    <Table.Column
                        key="firstName"
                        dataIndex="firstName"
                        title={<FormattedMessage id="first_name"/>}
                        sorter={(a: any, b: any) => (a.firstName || '').localeCompare(b.firstName || '')}
                    />
                    <Table.Column
                        key="lastName"
                        dataIndex="lastName"
                        title={<FormattedMessage id="last_name"/>}
                        sorter={(a: any, b: any) => (a.lastName || '').localeCompare(b.lastName || '')}
                    />
                    <Table.Column
                        key="phone"
                        dataIndex="phone"
                        title={<FormattedMessage id="phone"/>}
                        sorter={(a: any, b: any) => (a.phone || '').localeCompare(b.phone || '')}
                    />
                    <Table.Column
                        key="country"
                        dataIndex="country"
                        title={<FormattedMessage id="country"/>}
                        sorter={(a: any, b: any) => (a.country || '').localeCompare(b.country || '')}
                    />
                    <Table.Column
                        key="accountStatus"
                        dataIndex="accountStatus"
                        title={<FormattedMessage id="account_status"/>}
                        sorter={(a: any, b: any) => (a.accountStatus || '').localeCompare(b.accountStatus || '')}
                    />
                    <Table.Column
                        key="lastLogin"
                        dataIndex="lastLogin"
                        title={<FormattedMessage id="last_login_label"/>}
                        sorter={(a: any, b: any) => {
                            const dateA = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
                            const dateB = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
                            return dateA - dateB;
                        }}
                    />
                    <Table.Column
                        key="loginCount"
                        dataIndex="loginCount"
                        title={<FormattedMessage id="login_count_label"/>}
                        sorter={(a: any, b: any) => {
                            const countA = parseInt(a.loginCount) || 0;
                            const countB = parseInt(b.loginCount) || 0;
                            return countA - countB;
                        }}
                    />
                </Table>
                )}
            </Card>
        </AccessControl>
    )
};

export default Users;